import React, { useRef, useMemo, useCallback, useState, Key, useEffect, ReactNode } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule, LicenseManager, ModuleRegistry } from 'ag-grid-enterprise';
import { GridOptions, IServerSideDatasource } from 'ag-grid-community';
import { v4 as uuidv4 } from 'uuid';
import LoadingIndicator from 'components/loadingIndicator';
import { CustomHeaderSelectAll } from './customHeaderSelectAll';
import { agGridLicenseKey } from './agGridLicenseKey';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './ag-theme-eh.css';
import { IGridColumn } from './types';
import { mapToAgGridColumns } from './gridColumnMapper';
import { dataGridDefaultValues } from './dataGridDefaultValues';

ModuleRegistry.registerModules([AllEnterpriseModule]);

LicenseManager.setLicenseKey(agGridLicenseKey);

export interface DataGridDefaultValues {
	autoSizeAllColumns: boolean;
	suppressRowClickSelection: boolean;
}

interface DataGridProps<TData> {
	autoHeight?: boolean;
	columnDefs: IGridColumn[];
	customGridSearch?: ReactNode;
	data?: TData[];
	defaultPageSize?: number;
	defaultSort?: { colId: string; sort: 'asc' | 'desc' } | null;
	editable?: boolean;
	enableCellTextSelection?: boolean;
	enableColumnFilters?: boolean;
	gridHeading?: string;
	getRowClass?: (params: any) => string;
	getRowHeight?: any;
	height?: number | 'auto';
	gridKey?: Key | null;
	noRowsMessage?: string;
	pagination?: boolean;
	resizable?: boolean;
	sortable?: boolean;
	setSelectedRows?: (selectedList: any[] | undefined) => void;
	serverSideDatasource?: IServerSideDatasource;
	suppressRowClickSelection?: boolean;
	onRowClick?: (event: any, selectedRow: any) => void;
	childrenColumnDefs?: any;
	onCellEdit?: (event: any) => void;
	singleClickEdit?: boolean;
	rowIndex?: number;
	fitCellContents?: boolean;
	suppressColumnVirtualisation?: boolean;
}

export function DataGrid<TData>({
	columnDefs,
	customGridSearch,
	data = [],
	defaultSort = null,
	defaultPageSize = 10,
	editable = false,
	enableCellTextSelection = true,
	enableColumnFilters = false,
	gridHeading,
	gridKey,
	getRowClass,
	getRowHeight,
	height = 'auto',
	noRowsMessage = 'There are no results',
	pagination = true,
	resizable = true,
	sortable = true,
	setSelectedRows,
	serverSideDatasource,
	suppressRowClickSelection = dataGridDefaultValues.suppressRowClickSelection,
	onRowClick,
	childrenColumnDefs,
	onCellEdit,
	singleClickEdit = false,
	rowIndex = 0,
	fitCellContents = false,
	suppressColumnVirtualisation = false,
}: DataGridProps<TData>) {
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [pageSize, setPageSize] = useState(defaultPageSize);
	const gridRef = useRef<AgGridReact>(null);

	const containerStyle = { height: height };

	const agColumnDefs = mapToAgGridColumns(columnDefs);

	const gridOptions = useMemo<GridOptions>(() => {
		const options: GridOptions = {
			theme: 'legacy',
			suppressColumnVirtualisation: suppressColumnVirtualisation,
			components: {
				CustomHeaderSelectAll: CustomHeaderSelectAll,
			},
			defaultColDef: {
				resizable: resizable,
				sortable: sortable,
				editable: editable,
				filter: enableColumnFilters,
			},
			...(onCellEdit && {
				readOnlyEdit: true,
				onCellEditRequest: onCellEdit,
				singleClickEdit: singleClickEdit,
			}),
			...(childrenColumnDefs && {
				masterDetail: true,
				detailCellRendererParams: childrenColumnDefs,
				detailRowAutoHeight: true,
			}),
			domLayout: 'autoHeight',
			getRowHeight: getRowHeight,
			loadingOverlayComponent: LoadingIndicator,
			noRowsOverlayComponent: NoRowsOverlay,
			noRowsOverlayComponentParams: { noRowsMessage: noRowsMessage },
			suppressPaginationPanel: !pagination,
			paginationPageSize: pageSize,
			paginationPageSizeSelector: [10, 25, 50, 100],
			suppressContextMenu: true,
			onGridReady: (params) => {
				if (isInitialLoad) {
					if (defaultSort) {
						const columnState = [
							{
								colId: defaultSort.colId,
								sort: defaultSort.sort,
							},
						];
						params.api.applyColumnState({ state: columnState });
					}
					setIsInitialLoad(false);
				}
			},
			onFirstDataRendered: (params) => {
				if (dataGridDefaultValues.autoSizeAllColumns || fitCellContents) {
					params.api.autoSizeAllColumns();
				} else {
					params.api.sizeColumnsToFit();
				}
			},
			...(serverSideDatasource && {
				cacheBlockSize: pageSize,
				maxBlocksInCache: 0,
				rowModelType: 'serverSide',
				getRowId: (params) => {
					// Ensure a unique ID is returned for each row
					return params?.data?.id?.toString() ?? uuidv4();
				},
				serverSideDatasource: serverSideDatasource,
			}),
			getRowClass: getRowClass,
			...(!suppressRowClickSelection && {
				rowSelection: {
					mode: 'singleRow',
					checkboxes: false,
					enableClickSelection: true,
				},
			}),
			...(serverSideDatasource === undefined && { rowData: data }),
		};
		return options;
	}, [
		resizable,
		sortable,
		editable,
		enableColumnFilters,
		getRowClass,
		getRowHeight,
		noRowsMessage,
		pagination,
		pageSize,
		serverSideDatasource,
		data,
		isInitialLoad,
		defaultSort,
	]);

	const onPaginationChanged = useCallback(() => {
		if (gridRef.current?.api) {
			const newPageSize = gridRef.current.api.paginationGetPageSize();
			if (newPageSize !== pageSize) {
				setPageSize(newPageSize);
				gridRef.current?.api.updateGridOptions({
					paginationPageSize: newPageSize,
					cacheBlockSize: newPageSize,
				});
				if (serverSideDatasource) {
					gridRef.current.api.refreshServerSide({ purge: true });
				}
			}
		}
	}, [pageSize, serverSideDatasource]);

	const onSelectionChanged = useCallback(() => {
		if (setSelectedRows) {
			const selectedRows = gridRef.current?.api.getSelectedRows();
			setSelectedRows(selectedRows);
		}
	}, [setSelectedRows]);

	const cellClicked = useCallback(
		(event: any) => {
			const colId = event.column.getId();
			if (!suppressRowClickSelection) {
				if (colId !== 'button') {
					if (onRowClick) {
						gridRef.current?.api.setGridOption('loading', true);
						const selectedRows = gridRef.current?.api.getSelectedRows();
						onRowClick(event.event, selectedRows?.[0]);
					}
				}
			}
		},
		[gridRef, onRowClick, suppressRowClickSelection],
	);

	useEffect(() => {
		if (gridRef.current && gridRef.current?.api && data && !serverSideDatasource) {
			gridRef.current?.api.applyTransaction({ update: data });
		}
	}, [data, serverSideDatasource]);

	const handleGridSizeChanged = useCallback(() => {
		if (gridRef.current?.api) {
			if (dataGridDefaultValues.autoSizeAllColumns || fitCellContents) {
				gridRef.current.api.autoSizeAllColumns();
			} else {
				gridRef.current.api.sizeColumnsToFit();
			}
			// Check and show the no rows overlay if needed
			const rowCount = gridRef.current.api.getDisplayedRowCount();

			if (rowCount === 0) {
				gridRef.current.api.showNoRowsOverlay();
			} else {
				gridRef.current.api?.setGridOption('loading', false);
			}
		}
	}, []);

	const handleGridReady = useCallback(() => {
		// Set row focus on recent cell edited after redraw rowdata
		if (rowIndex > 0 && gridRef.current?.props.rowData && gridRef.current?.props.rowData.length > rowIndex && onCellEdit) {
			let pageNumber = gridRef.current?.props.pagination && gridRef.current?.props.paginationPageSize ? Math.floor(rowIndex / gridRef.current?.props.paginationPageSize) : 0;
			gridRef.current?.api.paginationGoToPage(pageNumber);
			agColumnDefs[0].colId && gridRef.current?.api.setFocusedCell(rowIndex, agColumnDefs[0].colId);
		}
	}, [rowIndex]);

	return (
		<div className="eh-grid-container">
			{(gridHeading || customGridSearch) && (
				<div className="eh-grid-header">
					{gridHeading && <h4 className="eh-grid-heading">{gridHeading}</h4>}
					{customGridSearch && <div className="eh-grid-filter">{customGridSearch}</div>}
				</div>
			)}
			<div className="ag-theme-alpine ag-theme-eh" style={containerStyle}>
				<AgGridReact
					columnDefs={agColumnDefs}
					enableCellTextSelection={enableCellTextSelection}
					gridOptions={gridOptions}
					key={gridKey}
					onSelectionChanged={onSelectionChanged}
					pagination={pagination}
					paginationPageSize={pageSize}
					ref={gridRef}
					onCellClicked={cellClicked}
					onGridSizeChanged={handleGridSizeChanged}
					onPaginationChanged={onPaginationChanged}
					rowData={gridOptions.rowData}
					onGridReady={handleGridReady}
				/>
			</div>
		</div>
	);
}

const NoRowsOverlay: React.FC<React.PropsWithChildren<{ noRowsMessage: string }>> = (props) => {
	return <strong className="ag-overlay-no-rows-center">{props.noRowsMessage}</strong>;
};
